import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { WorkNote, JobWithNotes } from '../workItemTypes';
import { WorkNotesHandlers } from '../hooks/useWorkNotes';
import { formatTimestamp } from '../utils/workItemUtils';

export interface WorkNotesSectionProps {
  job: JobWithNotes;
  workNotesHandlers: WorkNotesHandlers;
}

export const WorkNotesSection: React.FC<WorkNotesSectionProps> = ({
  job,
  workNotesHandlers
}) => {
  const [newUpdate, setNewUpdate] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when component mounts or job changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [job.id]);

  const handleAddComment = () => {
    workNotesHandlers.handleAddComment(job, newUpdate);
    setNewUpdate('');
  };

  const handleAddAction = () => {
    workNotesHandlers.handleAddAction(job, newUpdate);
    setNewUpdate('');
  };

  const handleToggleCompletion = (noteId: string) => {
    workNotesHandlers.handleToggleActionCompletion(job, noteId);
  };

  // Sort notes by timestamp (newest first)
  const sortedNotes: WorkNote[] = (job.notes || []).sort((a: WorkNote, b: WorkNote) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <div className="workitem-details-section">
      <div className="section-header">
        <h4>Work Notes</h4>
      </div>
      
      <div className="add-updates-form">
        <textarea
          ref={textareaRef}
          value={newUpdate}
          onChange={(e) => setNewUpdate(e.target.value)}
          placeholder="Add a comment or action item..."
          rows={3}
        />
        <div className="form-actions">
          <button
            type="button"
            onClick={handleAddComment}
            disabled={!newUpdate.trim()}
            className="btn-secondary"
          >
            Add Comment
          </button>
          <button
            type="button"
            onClick={handleAddAction}
            disabled={!newUpdate.trim()}
            className="btn-primary"
          >
            Add Action
          </button>
        </div>
      </div>

      <div className="work-notes-container">
        {sortedNotes.length === 0 ? (
          <p className="no-notes">No work notes added yet.</p>
        ) : (
          sortedNotes.map((note: WorkNote) => (
            <div key={note.id} className={`work-note ${note.type}`}>
              <div className="work-note-header">
                <div className="work-note-meta">
                  <span className="work-note-type">
                    {note.type === 'action' ? 'Action' : 'Comment'}
                  </span>
                  <span className="work-note-author">{note.author}</span>
                  <span className="work-note-timestamp">
                    {formatTimestamp(note.timestamp)}
                  </span>
                </div>
                {note.type === 'action' && (
                  <button
                    type="button"
                    className={`action-toggle ${note.isCompleted ? 'completed' : 'pending'}`}
                    onClick={() => handleToggleCompletion(note.id)}
                    title={note.isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {note.isCompleted ? (
                      <CheckCircle style={{ fontSize: '20px' }} />
                    ) : (
                      <RadioButtonUnchecked style={{ fontSize: '20px' }} />
                    )}
                  </button>
                )}
              </div>
              <div className={`work-note-content ${note.isCompleted ? 'completed' : ''}`}>
                {note.content}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};